import { v4 as uuidv4 } from 'uuid';
import {
  ClusteringLabCreateGroupResponse,
  ClusteringLabListDuplicateSetsResponse,
  ClusteringLabListGroupsResponse,
  ClusteringLabListPhotosResponse,
  ClusteringLabListShareworthyResponse,
  ClusteringLabUpdateGroupResponse,
} from '@dazzle/api-client';
import { getApi } from 'Common/api/DazzleApi';
import { wrapMethod } from 'Common/api/DazzleInternalService';
import { getDazzleUserAgent, getDeviceId } from 'Common/util/Net';

const xDeviceID = getDeviceId();
const xUserAgent = getDazzleUserAgent();

export const listPhotos = wrapMethod(
  async (userLid: number, cursor?: string | null, limit: number = 200): Promise<ClusteringLabListPhotosResponse> => {
    const { data } = await getApi().clusteringLabPhotosListPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabListPhotosRequest: { user_lid: userLid, cursor: cursor ?? undefined, limit },
    });
    return data;
  },
  'clusteringLabPhotosListPost'
);

export const listGroups = wrapMethod(async (userLid: number): Promise<ClusteringLabListGroupsResponse> => {
  const { data } = await getApi().clusteringLabGroupsListPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    clusteringLabListGroupsRequest: { user_lid: userLid },
  });
  return data;
}, 'clusteringLabGroupsListPost');

export const createGroup = wrapMethod(
  async (
    userLid: number,
    label: string,
    photoIds: string[],
    parentGroupId?: string | null
  ): Promise<ClusteringLabCreateGroupResponse> => {
    const { data } = await getApi().clusteringLabGroupsCreatePost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabCreateGroupRequest: {
        user_lid: userLid,
        label,
        photo_ids: photoIds,
        parent_group_id: parentGroupId ?? undefined,
      },
    });
    return data;
  },
  'clusteringLabGroupsCreatePost'
);

export const updateGroup = wrapMethod(
  async (
    userLid: number,
    groupId: string,
    updates: { label?: string; representative_photo_id?: string }
  ): Promise<ClusteringLabUpdateGroupResponse> => {
    const { data } = await getApi().clusteringLabGroupsUpdatePost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabUpdateGroupRequest: { user_lid: userLid, group_id: groupId, ...updates },
    });
    return data;
  },
  'clusteringLabGroupsUpdatePost'
);

export const deleteGroup = wrapMethod(async (userLid: number, groupId: string): Promise<object> => {
  const { data } = await getApi().clusteringLabGroupsDeletePost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    clusteringLabDeleteGroupRequest: { user_lid: userLid, group_id: groupId },
  });
  return data;
}, 'clusteringLabGroupsDeletePost');

export const assignMembers = wrapMethod(
  async (userLid: number, groupId: string, photoIds: string[]): Promise<object> => {
    const { data } = await getApi().clusteringLabMembersAssignPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabAssignMembersRequest: { user_lid: userLid, group_id: groupId, photo_ids: photoIds },
    });
    return data;
  },
  'clusteringLabMembersAssignPost'
);

export const unassignMembers = wrapMethod(
  async (userLid: number, groupId: string, photoIds: string[]): Promise<object> => {
    const { data } = await getApi().clusteringLabMembersUnassignPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabUnassignMembersRequest: { user_lid: userLid, group_id: groupId, photo_ids: photoIds },
    });
    return data;
  },
  'clusteringLabMembersUnassignPost'
);

export const listDuplicateSets = wrapMethod(
  async (
    userLid: number,
    cursor?: string | null,
    limit: number = 50
  ): Promise<ClusteringLabListDuplicateSetsResponse> => {
    const { data } = await getApi().clusteringLabDuplicatesListSetsPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabListDuplicateSetsRequest: { user_lid: userLid, cursor: cursor ?? undefined, limit },
    });
    return data;
  },
  'clusteringLabDuplicatesListSetsPost'
);

export const markNotDuplicate = wrapMethod(
  async (userLid: number, setPhotoIds: string[], notDuplicatePhotoIds: string[]): Promise<object> => {
    const { data } = await getApi().clusteringLabDuplicatesMarkNotDuplicatePost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabMarkNotDuplicateRequest: {
        user_lid: userLid,
        set_photo_ids: setPhotoIds,
        not_duplicate_photo_ids: notDuplicatePhotoIds,
      },
    });
    return data;
  },
  'clusteringLabDuplicatesMarkNotDuplicatePost'
);

export const confirmDuplicates = wrapMethod(async (userLid: number, photoIds: string[]): Promise<object> => {
  const { data } = await getApi().clusteringLabDuplicatesConfirmPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    clusteringLabConfirmDuplicatesRequest: { user_lid: userLid, photo_ids: photoIds },
  });
  return data;
}, 'clusteringLabDuplicatesConfirmPost');

export const listShareworthyPhotos = wrapMethod(
  async (
    userLid: number,
    cursor?: string | null,
    limit: number = 100
  ): Promise<ClusteringLabListShareworthyResponse> => {
    const { data } = await getApi().clusteringLabShareworthyListPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabListShareworthyRequest: { user_lid: userLid, cursor: cursor ?? undefined, limit },
    });
    return data;
  },
  'clusteringLabShareworthyListPost'
);

export const confirmShareworthy = wrapMethod(
  async (userLid: number, photoIds: string[], isShareworthy: boolean): Promise<object> => {
    const { data } = await getApi().clusteringLabShareworthyConfirmPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      clusteringLabConfirmShareworthyRequest: { user_lid: userLid, photo_ids: photoIds, is_shareworthy: isShareworthy },
    });
    return data;
  },
  'clusteringLabShareworthyConfirmPost'
);

export const exportData = wrapMethod(async (userLid: number): Promise<unknown> => {
  const { data } = await getApi().clusteringLabExportPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    clusteringLabExportRequest: { user_lid: userLid },
  });
  return data;
}, 'clusteringLabExportPost');
